import { useEffect, useState } from 'react';
import { AppState } from 'react-native';
import { useSelector } from '@legendapp/state/react';
import { MMKV } from 'react-native-mmkv';
import { globalState } from '@/state/globalState';
import { supabase, createSessionBoundSupabaseClient } from '@/services/supabase';
import {
  getSyncClientBuild,
  GPS_ADDRESS_FEATURE,
  subscribeSyncCanaryFeatureDecisions,
  syncCanaryFeatureDecisionStore,
} from '@/services/sync/SyncCanaryFeatureService';

let storage: MMKV | undefined;
const getStorage = () => storage ??= new MMKV({ id: 'gps-address-capability-v1' });

/** A ajuda GPS exige habilitação da empresa e cache pertencente à mesma sessão lógica. */
export const useGpsAddressCapability = (companyId: string): boolean => {
  const identity = useSelector(() => JSON.stringify([
    globalState.user.isAuthenticated.get(),
    globalState.user.profile.get()?.id,
    globalState.user.profile.get()?.empresa_id,
    globalState.user.session.get()?.user?.id,
  ]));
  const isOnline = useSelector(() => globalState.network.isConnected.get());
  const build = getSyncClientBuild();
  const scope = JSON.stringify([companyId, identity, build]);
  const [decision, setDecision] = useState({ scope: '', enabled: false });

  useEffect(() => {
    const [authenticated, userId, profileCompanyId, sessionUserId] = JSON.parse(identity);
    if (!authenticated || !userId || userId !== sessionUserId || companyId !== profileCompanyId || build < 142) return;
    let active = true;
    let generation = 0;
    const key = JSON.stringify([companyId, userId, build]);
    const matches = () => active
      && globalState.user.isAuthenticated.get() === true
      && globalState.user.profile.get()?.id === userId
      && globalState.user.profile.get()?.empresa_id === companyId
      && globalState.user.session.get()?.user?.id === userId;
    const cached = () => {
      try {
        if (syncCanaryFeatureDecisionStore.get({ companyId, featureKey: GPS_ADDRESS_FEATURE, clientBuild: build }) === false) return false;
        return getStorage().getString(key) === 'on';
      } catch { return false; }
    };
    setDecision({ scope, enabled: matches() && cached() });
    const unsubscribe = subscribeSyncCanaryFeatureDecisions(observed => {
      if (!matches() || observed.companyId !== companyId || observed.clientBuild !== build
        || observed.featureKey !== GPS_ADDRESS_FEATURE || observed.enabled) return;
      generation += 1;
      try { getStorage().set(key, 'off'); } catch { /* Revogar não depende da gravação do cache. */ }
      setDecision({ scope, enabled: false });
    });
    const refresh = async () => {
      if (!isOnline || !matches()) return;
      const request = ++generation;
      try {
        const { data: auth, error: authError } = await supabase.auth.getSession();
        if (authError || !auth.session?.access_token || auth.session.user.id !== userId || !matches()) return;
        const client = createSessionBoundSupabaseClient(auth.session.access_token);
        const { data, error } = await client.rpc('app_guincho_feature_enabled_v1', {
          p_empresa_id: companyId, p_feature_key: GPS_ADDRESS_FEATURE, p_client_build: build,
        });
        if (error || !matches() || request !== generation) return;
        const enabled = data === true;
        try { getStorage().set(key, enabled ? 'on' : 'off'); } catch { /* A decisão online permanece válida. */ }
        setDecision({ scope, enabled });
        try { syncCanaryFeatureDecisionStore.set({ companyId, featureKey: GPS_ADDRESS_FEATURE, clientBuild: build, enabled }); }
        catch { /* A decisão desta sessão já foi aplicada. */ }
      } catch { /* Sem resposta, somente a decisão anterior desta identidade pode habilitar GPS. */ }
    };
    void refresh();
    const listener = AppState.addEventListener('change', state => { if (state === 'active') void refresh(); });
    return () => { active = false; unsubscribe(); listener.remove(); };
  }, [companyId, identity, build, scope, isOnline]);

  return decision.scope === scope && decision.enabled;
};
